/**
 * Novidades (spec 036) no SPA: as marcas da pessoa (GET /news/state), as duas
 * escritas e o hook que junta tudo — quem vê o quê (D3), o contador e os
 * termos do tenant para o texto.
 *
 * Duas situações em que NADA é escrito:
 * - a aba "ver como" (spec 020): a API recusaria (403 impersonation_read_only)
 *   e o painel nem tenta — quem olha o portal como a Maria não marca nada como
 *   visto por ela;
 * - a API ainda sem /news (o SPA sobe no Pages minutos antes da API, D2 da
 *   regra de mudança de dois lados): o estado não carrega, o megafone fica sem
 *   número e as novidades que dependem do tenant ficam ocultas até a API subir.
 */
import { useCallback, useMemo } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NEWS_SEEN_BATCH_MAX, NO_NEWS_CAPABILITIES, type NewsMarks, type NewsState } from '@erp/shared';
import { api } from '@/lib/api-client';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { useIsImpersonating } from '@/auth/impersonation';
import { useTenantProfile } from '@/shared/tenant-profile';
import { sgrStatusQuery } from '@/features/sgr/sgr-queries';
import { NEWS_ENTRIES } from './entries';
import {
  newsTextVars,
  todayInSaoPaulo,
  unseenEntries,
  visibleEntries,
  type NewsAudience,
  type NewsEntry,
  type NewsRole,
  type NewsTextVars,
  type NewsViewer,
} from './news-model';

const NEWS_STATE_KEY = ['erp', 'news', 'state'] as const;

export const newsStateQuery = () =>
  queryOptions({
    queryKey: NEWS_STATE_KEY,
    queryFn: async ({ signal }): Promise<NewsState> => {
      const res = await api<NewsState>('/news/state', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    // segue a pessoa entre máquinas: a outra aba/máquina marca, esta relê
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

const ROLES: readonly NewsRole[] = ['admin', 'attendant', 'client'];

export interface UseNews {
  /** o estado da API chegou (sem ele, nada de contador e nada de escrita) */
  ready: boolean;
  visible: NewsEntry[];
  unseen: NewsEntry[];
  seenIds: ReadonlySet<string>;
  toursDone: ReadonlySet<string>;
  vars: NewsTextVars;
  /** marca como vistas (as que ainda não estão); nunca na aba "ver como" */
  markSeen: (ids: readonly string[]) => void;
  markTourDone: (tourId: string) => void;
}

/** O megafone, o painel, a página e o tour usam o MESMO cálculo. */
export function useNews(audience: NewsAudience): UseNews {
  const qc = useQueryClient();
  const user = useAuthUser();
  const { can, ready: permissionsReady } = usePermissions();
  const profile = useTenantProfile();
  const impersonating = useIsImpersonating();
  const stateQ = useQuery(newsStateQuery());
  // o nome do sistema de origem vem da API (o SPA não o escreve); o portal não o lê
  const sgrQ = useQuery({ ...sgrStatusQuery(), enabled: audience === 'staff' });

  const state = stateQ.data ?? null;
  const role = ROLES.find((r) => r === user?.role) ?? null;

  const visible = useMemo(() => {
    // enquanto o /me não resolveu, `can` libera tudo: esperar é melhor que mostrar e sumir
    if (!permissionsReady || stateQ.isPending) return [];
    const viewer: NewsViewer = {
      audience,
      role,
      can,
      capabilities: state?.capabilities ?? NO_NEWS_CAPABILITIES,
      resourceSource: profile.resource.source,
    };
    return visibleEntries(NEWS_ENTRIES, viewer);
  }, [audience, role, can, permissionsReady, stateQ.isPending, state, profile.resource.source]);

  const seenIds = useMemo(() => new Set(state?.seenIds ?? []), [state]);
  const toursDone = useMemo(() => new Set(state?.toursDone ?? []), [state]);
  const unseen = useMemo(
    () => (state ? unseenEntries(visible, state.seenIds, todayInSaoPaulo()) : []),
    [visible, state]
  );
  const vars = useMemo(
    () => newsTextVars(profile.terms, sgrQ.data?.sourceName ?? ''),
    [profile.terms, sgrQ.data?.sourceName]
  );

  const writable = state !== null && !impersonating;

  const seenM = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api<NewsMarks>('/news/seen', { method: 'POST', body: { ids } });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (marks) => qc.setQueryData<NewsState>(NEWS_STATE_KEY, (old) => (old ? { ...old, ...marks } : old)),
    // falhou: relê, e o contador volta ao que o servidor tem
    onError: () => void qc.invalidateQueries({ queryKey: NEWS_STATE_KEY }),
  });

  const tourM = useMutation({
    mutationFn: async (tourId: string) => {
      const res = await api<NewsMarks>(`/news/tours/${encodeURIComponent(tourId)}/done`, { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (marks) => qc.setQueryData<NewsState>(NEWS_STATE_KEY, (old) => (old ? { ...old, ...marks } : old)),
    onError: () => void qc.invalidateQueries({ queryKey: NEWS_STATE_KEY }),
  });
  const { mutate: sendSeen } = seenM;
  const { mutate: sendTour } = tourM;

  const markSeen = useCallback(
    (ids: readonly string[]) => {
      if (!writable) return;
      const fresh = [...new Set(ids)].filter((id) => !seenIds.has(id));
      if (fresh.length === 0) return;
      // otimista: o número some na hora; o POST confirma (ou o onError relê)
      qc.setQueryData<NewsState>(NEWS_STATE_KEY, (old) =>
        old ? { ...old, seenIds: [...old.seenIds, ...fresh] } : old
      );
      for (let i = 0; i < fresh.length; i += NEWS_SEEN_BATCH_MAX) sendSeen(fresh.slice(i, i + NEWS_SEEN_BATCH_MAX));
    },
    [writable, seenIds, qc, sendSeen]
  );

  const markTourDone = useCallback(
    (tourId: string) => {
      if (!writable || toursDone.has(tourId)) return;
      sendTour(tourId);
    },
    [writable, toursDone, sendTour]
  );

  return { ready: state !== null, visible, unseen, seenIds, toursDone, vars, markSeen, markTourDone };
}
